import { skipToken, useQuery, useQueries } from '@tanstack/react-query';
import { orpc } from '@/api/rpc/client';
import type { ReferenceEntityType } from '@erp/shared-types';

/**
 * Fetch a single external system reference by ID.
 */
export function useExternalSystemReference(id: string | undefined) {
  const query = useQuery({
    ...orpc.core.externalSystemReferences.get.queryOptions({
      input: id ? { id } : skipToken,
    }),
    enabled: Boolean(id),
  });

  return {
    ...query,
    data: query.data?.reference ?? null,
  };
}

/**
 * Fetch all external system references for a given entity.
 */
export function useExternalSystemReferencesByEntity(
  entityType: ReferenceEntityType | undefined,
  entityId: string | undefined,
) {
  const enabled = Boolean(entityType) && Boolean(entityId);

  const query = useQuery({
    ...orpc.core.externalSystemReferences.listByEntity.queryOptions({
      input: entityType && entityId ? { entityType, entityId } : skipToken,
    }),
    enabled,
  });

  return {
    ...query,
    references: query.data?.references ?? [],
  };
}

/**
 * Fetch external system references for multiple entities in parallel.
 */
export function useExternalSystemReferencesByEntities(
  entities: { entityType: ReferenceEntityType; entityId: string }[],
) {
  const queries = useQueries({
    queries: entities.map(({ entityType, entityId }) =>
      orpc.core.externalSystemReferences.listByEntity.queryOptions({
        input: { entityType, entityId },
      }),
    ),
  });

  return {
    isLoading: queries.some((q) => q.isLoading),
    references: queries.flatMap((q) => q.data?.references ?? []),
  };
}
